/**
 * This page provides an overview of the registered models for either one entity
 * or a combined registry overview across the user's teams and personal registered models.
 */
// Due to a scaling issue with the weave query used on this page, we're splitting the component
// into 2 parts.
// 1. All-teams component - this will use the weave query to fetch all the collections from all
// the viewer's teams (in the current organization's context), while we work on setting up the
// backend endpoints to use gql queries directly
// 2. Single-team component - this will use the new hook to fetch the team's collections incrementally

import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {useEffect} from 'react';
import {useHistory, useParams} from 'react-router';

import {captureError} from '../../integrations';
import {useViewer} from '../../state/viewer/hooks';
import {trackPageViewed} from '../../util/navigation';
import * as urls from '../../util/urls';
import {ErrorBoundary} from '../ErrorBoundary';
import {SelectEntity} from '../Launch/Queues/SelectEntity';
import {SingleRegistrySearchContextProvider} from '../Registries/common/RegistriesSearchContextProvider';
import {ModelRegistryMigrationContextProvider} from '../Registries/ModelRegistryMigration/ModelRegistryMigrationContextProvider';
import {EntryPoint} from '../Registries/ModelRegistryMigration/utils';
import {AllTeamsModelRegistryPage} from './AllTeamsModelRegistryPage';
import * as S from './ModelRegistryPage.styles';
import {ModelRegistryPageHeader} from './ModelRegistryPageHeader';
import {SingleTeamModelRegistryPage} from './SingleTeamModelRegistryPage';
import {ZeroModels} from './ZeroModels';
type ModelRegistryPageParams = {
  entityName?: string;
  registryName?: string;
};

function logError(error: Error) {
  console.error('All teams model registry page error', error);
  captureError(error, 'AllTeamsModelRegistryPage error');
}

export const ModelRegistryPage = () => {
  // for page view tracking
  useEffect(() => {
    trackPageViewed('New model registry homepage');
  }, []);
  const {entityName} = useParams<ModelRegistryPageParams>();
  const viewer = useViewer();
  const history = useHistory();
  const username = viewer?.username;

  function renderPanelError() {
    // User changed entity filter selection
    const onSelectEntity = (name: string) => {
      const url = name
        ? urls.objectRegistry('model', undefined, name)
        : urls.objectRegistry('model', undefined);
      history.push(url);
    };
    return (
      <>
        <S.Panel>
          <ModelRegistryPageHeader />
          <S.OptionsPanel>
            <S.SelectPanel>
              {/*Rendering the select-entity dropdown in the error panel without the all-teams option since that's what errored out*/}
              <SelectEntity
                size="large"
                value={entityName ?? ''}
                onSelectEntity={onSelectEntity}
              />
            </S.SelectPanel>
          </S.OptionsPanel>
          <div style={{padding: 40}}>
            <PanelError
              message={
                <div>
                  Loading this content has failed due to the size and number of
                  your collections. We recommend selecting an individual team to
                  view collection information.
                </div>
              }
              logErrorMessage="Loading this content has failed due to the size and number of your collections. We recommend selecting an individual team to view collection information."
            />
          </div>
        </S.Panel>
      </>
    );
  }

  // Anon user trying to view their own registry
  // wandb.ai/models or wandb.ai/registry/model
  if (!username && !entityName) {
    return (
      <S.Panel>
        <ModelRegistryPageHeader />
        <ZeroModels onCreateModel={() => {}} />
      </S.Panel>
    );
  }

  if (entityName == null) {
    return (
      <ModelRegistryMigrationContextProvider entryPoint={EntryPoint.LEGACY_MR}>
        <ErrorBoundary renderError={renderPanelError} onError={logError}>
          <AllTeamsModelRegistryPage />
        </ErrorBoundary>
      </ModelRegistryMigrationContextProvider>
    );
  }

  return (
    <ModelRegistryMigrationContextProvider
      entryPoint={EntryPoint.LEGACY_MR}
      mrEntityName={entityName}>
      <SingleRegistrySearchContextProvider>
        <SingleTeamModelRegistryPage entityName={entityName} />
      </SingleRegistrySearchContextProvider>
    </ModelRegistryMigrationContextProvider>
  );
};
